import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api, setToken, getToken } from "../lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const [saved, setSaved] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [membership, setMembership] = useState(null);
  const refreshMembership = useCallback(async () => {
    try { const { data } = await api.get("/me/membership"); setMembership(data); } catch { setMembership(null); }
  }, []);

  const refreshMemberData = useCallback(async () => {
    try {
      const [s, r, rp, en, refl, pu] = await Promise.all([
        api.get("/me/saved"),
        api.get("/me/rsvps"),
        api.get("/me/recent-plays"),
        api.get("/me/enrollments"),
        api.get("/me/reflections"),
        api.get("/me/purchases").catch(() => ({ data: [] })),
      ]);
      api.get("/me/membership").then((m) => setMembership(m.data)).catch(() => setMembership(null));
      setSaved(s.data || []);
      setRsvps(r.data || []);
      setRecentlyPlayed((rp.data || []).map((x) => x.snapshot || x));
      setEnrollments(en.data || []);
      setReflections(refl.data || []);
      setPurchases(pu.data || []);
    } catch (e) { /* silent */ }
  }, []);

  const refreshPurchases = useCallback(async () => {
    try {
      const { data } = await api.get("/me/purchases");
      setPurchases(data || []);
    } catch { /* silent */ }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = getToken();
    if (!token) { setAuthLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshMemberData();
    } catch { setToken(null); }
    finally { setAuthLoading(false); }
  }, [refreshMemberData]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token); setUser(data.user);
    await refreshMemberData();
    return data.user;
  }, [refreshMemberData]);

  const login = useCallback(async (payload) => {
    const { data } = await api.post("/auth/login", payload);
    setToken(data.token); setUser(data.user);
    await refreshMemberData();
    return data.user;
  }, [refreshMemberData]);

  const logout = useCallback(() => {
    setToken(null); setUser(null);
    setSaved([]); setRsvps([]); setRecentlyPlayed([]); setEnrollments([]); setReflections([]); setPurchases([]); setMembership(null);
  }, []);

  const closeTrack = useCallback(() => {
    setIsPlaying(false);
    setCurrentTrack(null);
    setProgress(0);
  }, []);

  const playTrack = useCallback((track) => {
    setCurrentTrack(track);
    setIsPlaying(true);
    setProgress(0);
    if (user) {
      const snap = { id: track.id, title: track.title, host: track.host, duration: track.duration, art: track.art || track.art_url, kind: track.kind };
      api.post("/me/recent-plays", { teaching_id: track.id, snapshot: snap })
        .then(() => api.get("/me/recent-plays"))
        .then((r) => setRecentlyPlayed((r.data || []).map((x) => x.snapshot || x)))
        .catch(() => {});
    } else {
      setRecentlyPlayed((prev) => {
        const filtered = prev.filter((t) => t.id !== track.id);
        return [track, ...filtered].slice(0, 6);
      });
    }
  }, [user]);

  const togglePlay = useCallback(() => setIsPlaying((p) => !p), []);

  const toggleSave = useCallback(async (item, item_type = "teaching") => {
    const snap = { id: item.id, title: item.title, host: item.host || item.author, art: item.art || item.art_url, kind: item.kind || item.type, type: item.type };
    if (!user) {
      setSaved((prev) => {
        const exists = prev.find((s) => s.item_ref === item.id);
        if (exists) return prev.filter((s) => s.item_ref !== item.id);
        return [{ item_type, item_ref: item.id, snapshot: snap }, ...prev];
      });
      return;
    }
    try {
      await api.post("/me/saved", { item_type, item_ref: item.id, snapshot: snap });
      const { data } = await api.get("/me/saved");
      setSaved(data || []);
    } catch {}
  }, [user]);

  const refreshRsvps = useCallback(async () => {
    if (!user) return;
    try { const { data } = await api.get("/me/rsvps"); setRsvps(data || []); } catch {}
  }, [user]);

  const toggleRsvp = useCallback(async (event) => {
    const snap = { id: event.id, title: event.title, date: event.date, time: event.time || event.start_time, format: event.format };
    if (!user) {
      setRsvps((prev) => {
        const exists = prev.find((r) => r.event_id === event.id);
        if (exists) return prev.filter((r) => r.event_id !== event.id);
        return [{ event_id: event.id, snapshot: snap }, ...prev];
      });
      return;
    }
    try {
      await api.post("/me/rsvps", { event_id: event.id, snapshot: snap });
      const { data } = await api.get("/me/rsvps");
      setRsvps(data || []);
    } catch {}
  }, [user]);

  const addReflection = useCallback(async (payload) => {
    if (!user) throw new Error("Sign in to save reflections");
    const { data } = await api.post("/me/reflections", payload);
    const { data: list } = await api.get("/me/reflections");
    setReflections(list || []);
    return data;
  }, [user]);

  const updateReflection = useCallback(async (rid, text) => {
    await api.patch(`/me/reflections/${rid}`, { text });
    const { data: list } = await api.get("/me/reflections");
    setReflections(list || []);
  }, []);

  const deleteReflection = useCallback(async (rid) => {
    await api.delete(`/me/reflections/${rid}`);
    setReflections((prev) => prev.filter((r) => r.id !== rid));
  }, []);

  const isSaved = useCallback((itemId) => saved.some((s) => s.item_ref === itemId || s.id === itemId), [saved]);
  const isRsvped = useCallback((eventId) => rsvps.some((r) => r.event_id === eventId || r.id === eventId), [rsvps]);

  return (
    <AppContext.Provider
      value={{
        user, authLoading, register, login, logout,
        currentTrack, isPlaying, progress, setProgress, playTrack, togglePlay, setIsPlaying,
        saved, toggleSave, isSaved,
        rsvps, toggleRsvp, isRsvped, refreshRsvps,
        recentlyPlayed, enrollments,
        reflections, addReflection, updateReflection, deleteReflection,
        purchases, refreshPurchases,
        membership, refreshMembership,
        refreshMemberData, closeTrack,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
